import React, {useState, useEffect, useRef} from 'react';
import {useForm} from 'react-hook-form';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTitle} from '../../../components/General/Layout';
import LoadingWheel from '../../../components/General/LoadingWheel';
import MaintenanceHeader from '../../../components/Surveys/SurveyMaintenanceHeader';
import ErrorHelper from '../../../service/ErrorHelper';
import {SMaster, Survey} from '../../../service/Urls';
import {get, post} from '../../../service/ApiService';
import SatisfactionSection from '../../../components/Surveys/SatisfactionSection';
import {Box, Flex, Heading, Spacer, Text, Textarea} from '@chakra-ui/react';
import SurveySideBar from '../../../components/Surveys/SurveySideBar';
import FlexCard from '../../../components/General/FlexCard';
import SatisfactionToTen from '../../../components/Surveys/SatisfactionToTen';
import {CustomCheckbox, CustomIntRadioGroup} from '../../../components/General/FormInputs';
import BoxCard from '../../../components/General/BoxCard';
import {formatDate} from '../../../helpers/Functions';
import PreArrangeRadio from '../../../components/Surveys/PreArrangeRadio';

const CemetaryMaintenance = () => {
  const [data, setData] = useState({});
  const [smasterData, setSmasterData] = useState({});
  const [toast, setToast] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(true);
  const [ids, setIds] = useState([]);

  const [title, setTitle] = useTitle('Cemetery Survey Maintenance');

  const isMounted = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: {errors},
  } = useForm();

  useEffect(() => {
    isMounted.current = true;

    loadData(location?.state?.id);

    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadData = async masterId => {
    const data = await get(Survey.GET + `/` + masterId);
    setToast(data);
    if (!data.error) {
      if (!data.dateAdded) {
        data.dateAdded = localStorage.getItem('prevDateAdded') || new Date().toISOString().split('T')[0];
        setIsUpdate(false);
      } else {
        // For a datepicker to default a value it must be in yyyy-mm-dd.  Modify initial value then call reset on the data to get that defaulted
        data.dateAdded = data.dateAdded.split('T')[0];
      }
      setData(data);
      reset(data);
      const smasterResponse = await get(SMaster.RECORD + '/' + masterId);
      setToast(smasterResponse);
      if (!smasterResponse.error) {
        smasterResponse['contractDate'] = formatDate(smasterResponse.smCondate);
        setSmasterData(smasterResponse);
      }
    }
    setIsLoading(false);
    setTitle('Cemetery Survey Maintenance');
    assignIds();
  };

  const assignIds = () => {
    let arr = [];
    const prompts = [prompt2, prompt3, prompt4, prompt5, prompt6];
    prompts.forEach(promptArr => {
      arr = arr.concat(promptArr.map(prompt => prompt.id));
    });
    setIds(arr);
  };

  const submitSurvey = async formData => {
    formData.svRecord = data.svRecord ?? smasterData.smRecord;
    formData.svClient = data.svClient ?? smasterData.smClient;
    formData.svLoc = data.svLoc ?? smasterData.smLoc;
    formData.svGroup = data.svGroup ?? smasterData.smGroup;

    formData.clientId = smasterData.nclientpk;
    formData.groupId = smasterData.ngrouppk;
    formData.locationId = smasterData.locationpk;
    formData.sMasterId = smasterData.npk;

    formData.svPos = data.svPos;
    formData.svNeg = data.svNeg;
    formData.svNeedatt = data.svNeedatt;

    let url = Survey.ADD_CEMETARY;
    let successMessage = 'Successfully added survey for ' + smasterData.clientName;
    if (isUpdate) {
      url = Survey.UPDATE_CEMETARY + '/' + formData.id;
      successMessage = 'Successfully updated survey for ' + smasterData.clientName;
    }

    setIsSaving(true);

    const response = await post(url, formData);

    if (isMounted.current === false) return;

    setIsSaving(false);

    setToast({
      response: response,
      successMessage: successMessage,
      onSuccess: () => navigate('/surveys/lookup', {state: {serviceType: location?.state?.serviceType}}),
    });
  };

  // The ids must correspond to the data that is sent from api, otherwise defaults and sending data up won't work
  const prompt2 = [
    {prompt: '2a. Response', id: 'sv2a'},
    {prompt: 'b. Greet', id: 'sv2b'},
    {prompt: 'c. Care', id: 'sv2c'},
    {prompt: 'd. Facilities', id: 'sv2d'},
    {prompt: 'e. Ground', id: 'sv2e'},
  ];

  const prompt3 = [
    {prompt: '3a. Needs', id: 'sv3a'},
    {prompt: 'b. Wishes', id: 'sv3b'},
    {prompt: 'c. Explain', id: 'sv3c'},
    {prompt: 'd. Detail', id: 'sv3d'},
    {prompt: 'e. Knowledge', id: 'sv3e'},
    {prompt: 'f. Sincere', id: 'sv3f'},
    {prompt: 'g. Answers', id: 'sv3g'},
    {prompt: 'h. Payment', id: 'sv3h'},
  ];

  const prompt4 = [
    {prompt: '4a. Grounds Staff', id: 'sv4a'},
    {prompt: 'b. Grave Staff', id: 'sv4b'},
  ];

  const prompt5 = [
    {prompt: '5a. Service Needs', id: 'sv5a'},
    {prompt: 'b. Professionalism', id: 'sv5b'},
    {prompt: 'c. Service', id: 'sv5c'},
  ];

  const prompt6 = [
    {prompt: '6a. Value', id: 'sv6a'},
    {prompt: '7a. Satisfaction', id: 'sv7a'},
  ];

  const ExtraContent = () => {
    const list = [
      {key: 'Yes', value: 1},
      {key: 'No', value: 2},
      {key: 'N/A', value: 3},
    ];
    return (
      <Flex>
        <Heading size="sm" mr="58px">
          i. Follow Up
        </Heading>
        <CustomIntRadioGroup control={control} id="sv3j" errors={errors} list={list} defaultValue={data.sv3j} />
      </Flex>
    );
  };

  return (
    <>
      {isLoading ? (
        <>
          <LoadingWheel />
          <ErrorHelper toastResponse={toast} />
        </>
      ) : (
        <form onSubmit={handleSubmit(submitSurvey)}>
          <Box w={{base: '100%', lg: '90%'}}>
            <MaintenanceHeader
              register={register}
              control={control}
              errors={errors}
              masterData={smasterData}
              data={data}
              setData={setData}
              reset={reset}
              ids={ids}
              getValues={getValues}
              submit={handleSubmit(submitSurvey)}
            />

            <Flex>
              <Box w="40%">
                <BoxCard textAlign="start">
                  <Flex>
                    <Text mr="4">1.</Text>
                    <Box textAlign="start">
                      <CustomCheckbox name="Pre-Arranged" id={'sv1a'} control={control} defaultChecked={data.sv1a} />
                      <CustomCheckbox name="Recommendation" id={'sv1b'} control={control} defaultChecked={data.sv1b} />
                      <CustomCheckbox name="Location" id={'sv1c'} control={control} defaultChecked={data.sv1c} />
                      <CustomCheckbox name="Website" id={'sv1j'} control={control} defaultChecked={data.sv1j} />
                      <CustomCheckbox name="Other" id={'sv1h'} control={control} defaultChecked={data.sv1h} />
                    </Box>
                    <Spacer />
                    <Box textAlign="start">
                      <CustomCheckbox name="Reputation" id={'sv1d'} control={control} defaultChecked={data.sv1d} />
                      <CustomCheckbox name="Previous Experience" id={'sv1e'} control={control} defaultChecked={data.sv1e} />
                      <CustomCheckbox name="Advertising" id={'sv1f'} control={control} defaultChecked={data.sv1f} />
                      <CustomCheckbox name="Deceased Wishes" id={'sv1g'} control={control} defaultChecked={data.sv1g} />
                    </Box>
                  </Flex>
                  <Textarea rows={1} id="sv1hd" name="Recommendation" {...register('sv1hd')} errors={errors} />
                  <Text>Recommending Funeral Home</Text>
                  <Textarea rows={1} id="sv1k" name="Other" {...register('sv1k')} errors={errors} />
                </BoxCard>
                <SatisfactionSection control={control} prompts={prompt2} data={data} />
                <SatisfactionToTen control={control} question={{prompt: '8a. Recommendation', id: 'sv88a'}} data={data} />
              </Box>
              <Box w="40%">
                <SatisfactionSection control={control} prompts={prompt3} data={data} ExtraContent={<ExtraContent />} />
                <SatisfactionSection control={control} prompts={prompt4} data={data} discardHeader />
                <SatisfactionSection control={control} prompts={prompt5} data={data} discardHeader />
                <SatisfactionSection control={control} prompts={prompt6} data={data} discardHeader />
              </Box>

              <Box w="20%">
                <SurveySideBar
                  control={control}
                  includes={{chinese: false, surveyFormat: false, recommendations: false}}
                  data={data}
                  register={register}
                  getValues={getValues}
                  reset={reset}
                  setData={setData}
                  errors={errors}
                  svcType={location?.state?.serviceType}
                  sMasterId={location?.state?.id}
                  isSaving={isSaving}
                />
              </Box>
            </Flex>
            <FlexCard>
              <Heading size={'sm'}>9a. Pre-Arrange</Heading>
              <PreArrangeRadio control={control} id="sv9a" name="9a. Pre-Arrange" data={data} />
            </FlexCard>
            <FlexCard>
              <Textarea rows={3} id="svComment" name="svComment" {...register('svComment')} errors={errors} placeholder="Comments" />
            </FlexCard>
          </Box>
          <ErrorHelper toastResponse={toast} />
        </form>
      )}
    </>
  );
};

export default CemetaryMaintenance;
